import React from "react";
import { motion } from "framer-motion";
import { PlugZap, Check, Info } from "lucide-react";

const integrations = [
  {
    name: "Dentrix",
    logo: "https://framerusercontent.com/images/FK2PxMGUliSNEptVBpWhWGjhW4.png",
    blurb: "Full read/write integration — schedules, ledgers, claims, notes.",
  },
  {
    name: "Eaglesoft",
    logo: "https://framerusercontent.com/images/JN5q8qzSCZ7K4LwF01UYdhkXhmI.png",
    blurb: "Native connector — verification, claims, and patient records in sync.",
  },
  {
    name: "Open Dental",
    logo: "https://framerusercontent.com/images/rlsK5y9oz2tQ5YKXeUw3yHuCHmQ.png",
    blurb: "API-level integration — real-time scheduling, billing, and charting.",
  },
];

const capabilities = [
  "Scheduling & appointments",
  "Patient records, documents & forms",
];

const IntegrationPartnersSlide = () => {
  return (
    <div className="h-full w-full bg-[#f8fafc] flex flex-col items-center justify-center p-8 xl:p-12 relative overflow-hidden font-sans">
      {/* Ambient gradient blurs */}
      <div
        className="absolute top-[-200px] right-[-100px] w-[700px] h-[700px] rounded-full blur-[120px] opacity-30 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #93c5fd 0%, #c4b5fd 50%, #fbcfe8 100%)" }}
      />
      <div
        className="absolute bottom-[-200px] left-[-100px] w-[600px] h-[600px] rounded-full blur-[120px] opacity-25 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #a5f3fc 0%, #93c5fd 50%, #c4b5fd 100%)" }}
      />
      <div className="absolute inset-0 bg-[radial-gradient(#cbd5e1_0.8px,transparent_0.8px)] [background-size:28px_28px] opacity-40 pointer-events-none" />

      <div className="w-full flex flex-col items-center relative z-10 max-w-[1120px]">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50 text-blue-600 text-sm font-semibold mb-3 border border-blue-100/60 shadow-sm">
            <PlugZap className="w-4 h-4" />
            <span>PMS Integrations</span>
          </div>
          <h2 className="text-3xl lg:text-[42px] font-bold text-slate-900 tracking-tight leading-tight">
            Continuous PMS integrations are offered with{" "}
            <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              the following PMS systems.
            </span>
          </h2>
          <p className="text-slate-900 text-[22px] font-semibold mt-6 max-w-2xl mx-auto leading-relaxed">
            If you're on one of these, you can keep your PMS and layer Asha's AI workforce on top —
            no migration required.
          </p>
        </motion.div>

        {/* Integration cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.5 }}
          className="w-full grid grid-cols-3 gap-5 mb-7"
        >
          {integrations.map((integration, i) => (
            <div
              key={i}
              className="bg-white rounded-2xl border border-slate-200 shadow-sm p-7 flex flex-col items-center text-center hover:shadow-md transition-shadow"
            >
              <div className="w-32 h-32 rounded-2xl bg-slate-50 border border-slate-100 flex items-center justify-center mb-4">
                <img
                  src={integration.logo}
                  alt={integration.name}
                  className="w-24 h-24 object-contain"
                />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-1.5">{integration.name}</h3>
              <p className="text-[15px] text-slate-700 leading-relaxed">{integration.blurb}</p>
            </div>
          ))}
        </motion.div>

        {/* Capabilities */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25, duration: 0.5 }}
          className="w-full rounded-2xl border border-slate-200 bg-white p-6"
        >
          <p className="text-[10.5px] font-bold text-slate-400 uppercase tracking-wider mb-3">
            What the integration covers
          </p>
          <div className="grid grid-cols-2 gap-x-6 gap-y-2.5">
            {capabilities.map((cap, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-full bg-blue-50 border border-blue-200 flex items-center justify-center shrink-0">
                  <Check className="w-3 h-3 text-blue-600" />
                </div>
                <span className="text-[13px] text-slate-700 font-medium">{cap}</span>
              </div>
            ))}
          </div>
        </motion.div>

        <p className="mt-5 flex items-center gap-2 text-[15px] text-slate-600 font-medium">
          <Info className="w-4 h-4" />
          <span>
            On a different PMS? We can migrate you to Asha's PMS — see the next slide for
            supported systems.
          </span>
        </p>
      </div>
    </div>
  );
};

export default IntegrationPartnersSlide;
